import { inject, Injectable } from '@angular/core'
import { Actions, createEffect, ofType } from '@ngrx/effects'
import { concatLatestFrom } from '@ngrx/operators'
import { setMetricFinderHolder, setMetricFinderMetrics } from '@core/store/actions/metric-finder.actions'
import { map, switchMap, tap } from 'rxjs/operators'
import { MetricsFinderApi } from '@core/services/metric-finder/metrics-finder.api'
import { Store } from '@ngrx/store'
import {
  fetchFavTeamPlayerMetrics,
  fetchMatchPlayerComparisonMetrics,
  fetchMatchPlayerMetrics,
  fetchPlayerComparisonMetrics,
  fetchPlayerMetrics,
  fetchPlayerMetricsFinderData
} from '@core/store/actions/player-metric-finder.actions'
import { mapComparisonResult, mapPlayer } from '@core/utils/player.utils'
import { requestErrorHandler } from '@shared/operators/request-error.operator'
import { TeamApi } from '@core/requests/api/team/team.api'
import { mapTeamSummary } from '@pages/team/utils/team.utils'
import { PlayerApi } from '@core/requests/api/player/player.api'
import {
  Player,
  PlayerComparison,
  PlayerComparisonRequest,
  PlayerComparisonRequestItem,
  PlayerSummary
} from '@core/models/player.models'
import { Params } from '@angular/router'
import { selectMergedRoute } from '@core/ngrx-router/state/selectors/ngrx-router.selectors'
import { preparePlayerComparison } from '@pages/metric-finder/utils/metric-finder.utils'
import { PlayerPosition } from '@core/enums/player.enums'
import {
  selectAggregation,
  selectAggregationsLeftAggregation,
  selectAggregationsRightAggregation
} from '@features/aggregations/store/selectors/aggregations.selectors'
import { MatchPlayerNgrxService } from '@shared/helpers/match-player-ngrx.service'
import { Aggregation, AggregationSelectable } from '@features/aggregations/models/aggregations.models'
import { MetricRequest } from '@core/models/metric.models'
import { parseStatics } from '@core/utils/assets.utils'
import { StaticOrientation } from '@core/enums/statics.enum'
import { Team } from '@core/models/team.models'
import { getAggregationSelectors, syncTeamAggregations } from '@features/aggregations/utils/aggregations.utils'

@Injectable()
export class PlayerMetricFinderEffects {
  private readonly _actions$: Actions = inject(Actions)
  private readonly _store: Store = inject(Store)
  private readonly _teamApi: TeamApi = inject(TeamApi)
  private readonly _playerApi: PlayerApi = inject(PlayerApi)
  private readonly _api: MetricsFinderApi = inject(MetricsFinderApi)
  private readonly _helper: MatchPlayerNgrxService = inject(MatchPlayerNgrxService)

  fetchPlayerMetricsFinderData$ = createEffect(() =>
    this._actions$.pipe(
      ofType(fetchPlayerMetricsFinderData),
      concatLatestFrom(() => [
        this._store.select(selectMergedRoute),
        this._store.select(selectAggregationsLeftAggregation),
        this._store.select(selectAggregationsRightAggregation),
        this._store.select(selectAggregation)
      ]),
      map(([, { params, queryParams }, leftAggregation, rightAggregation, aggregation]) => ({
        params,
        position: queryParams?.position,
        leftAggregation,
        rightAggregation,
        aggregation
      })),
      map(({ params, position, aggregation, leftAggregation, rightAggregation }) => {
        const isMatch = !!params.matchId
        const isVsEnabled = !!params.vsPlayerId
        if (isVsEnabled) {
          return this._resolvePlayerComparison(params, position, isMatch, aggregation)
        } else {
          return this._resolveSinglePlayer(params, position, isMatch, leftAggregation, rightAggregation)
        }
      })
    )
  )

  fetchFavTeamPlayerMetrics$ = createEffect(() =>
    this._actions$.pipe(
      ofType(fetchFavTeamPlayerMetrics),
      concatLatestFrom(() => getAggregationSelectors(this._store)),
      tap(([_, leftAggregation, rightAggregation, aggregationModes]) => {
        syncTeamAggregations(this._store, leftAggregation, rightAggregation, aggregationModes)
      }),
      switchMap(([{ playerId, playerPosition, seasonId, competitionId, teamId }]) =>
        this._playerApi.fetchPlayer({ playerId, seasonId, competitionId, teamId }).pipe(
          map(
            (summary) =>
              ({
                ...summary,
                playerId,
                metricPlayerPosition: playerPosition,
                teamId,
                seasonId,
                competitionId
              }) as PlayerSummary
          ),
          requestErrorHandler()
        )
      ),
      switchMap((summary) =>
        this._teamApi.fetchTeam(summary.seasonId, summary.competitionId, summary.teamId).pipe(
          map((team) => ({ ...mapPlayer(summary), team: mapTeamSummary(team) }) as PlayerSummary),
          requestErrorHandler()
        )
      ),
      map((player: PlayerSummary) => fetchPlayerMetrics({ player }))
    )
  )

  fetchPlayerMetrics$ = createEffect(() =>
    this._actions$.pipe(
      ofType(fetchPlayerMetrics),
      tap(({ player }) => this._store.dispatch(setMetricFinderHolder({ metricHolder: { player } }))),
      concatLatestFrom(() => [
        this._store.select(selectAggregationsLeftAggregation),
        this._store.select(selectAggregationsRightAggregation)
      ]),
      map(([{ player }, leftAggregation, rightAggregation]) =>
        this._getPlayerMetricRequestParams(player, leftAggregation.id, rightAggregation.id)
      ),
      switchMap((payload) => this._api.fetchPlayerMetrics(payload).pipe(requestErrorHandler())),
      map((metrics: any) => setMetricFinderMetrics({ metrics }))
    )
  )

  fetchPlayerComparisonMetrics$ = createEffect(() =>
    this._actions$.pipe(
      ofType(fetchPlayerComparisonMetrics),
      switchMap(({ players }) =>
        this._api.fetchPlayerComparisonMetrics(players).pipe(
          map((data) => mapComparisonResult(data)),
          map(({ playerA, playerB, ...metrics }) => ({
            ...metrics,
            playerA: { ...playerA, ...players.playerA },
            playerB: { ...playerB, ...players.playerB }
          })),
          map(({ playerA, playerB, ...metrics }) => {
            this._store.dispatch(setMetricFinderHolder({ metricHolder: { comparison: { playerA, playerB } } }))
            return metrics
          }),
          requestErrorHandler()
        )
      ),
      map((metrics) => setMetricFinderMetrics({ metrics }))
    )
  )

  fetchMatchPlayerMetrics$ = createEffect(() =>
    this._actions$.pipe(
      ofType(fetchMatchPlayerMetrics),
      switchMap(({ type: _type, ...payload }) => this._helper.resolveMatchPlayerSummary(payload, () => undefined)),

      switchMap(({ summary, metrics }) => [
        setMetricFinderHolder({ metricHolder: { player: mapPlayer(summary) } }),
        setMetricFinderMetrics({ metrics })
      ])
    )
  )

  fetchMatchPlayerComparisonMetrics$ = createEffect(() =>
    this._actions$.pipe(
      ofType(fetchMatchPlayerComparisonMetrics),
      switchMap(({ matchId, comparison, position }) =>
        this._teamApi
          .fetchTeam(comparison?.playerA?.seasonId, comparison?.playerA?.competitionId, comparison?.playerA?.teamId)
          .pipe(map((team) => ({ team: mapTeamSummary(team), matchId, position, comparison })))
      ),
      switchMap(({ matchId, comparison, position, team }) =>
        this._api
          .fetchMatchPlayerComparisonMetrics(matchId, comparison, position)
          .pipe(map((comparisonResult) => this._parsePlayerComparison(comparisonResult, comparison, team)))
      ),
      map(({ playerA, playerB, ...metrics }) => {
        this._store.dispatch(setMetricFinderHolder({ metricHolder: { comparison: { playerA, playerB } } }))
        return setMetricFinderMetrics({ metrics })
      })
    )
  )

  private _getPlayerMetricRequestParams(
    player: PlayerSummary,
    leftAggregationDataSource: Aggregation,
    aggregationDataSource: Aggregation
  ): MetricRequest {
    const { seasonId, competitionId, teamId, id: playerId, metricPlayerPosition: playerPosition } = player
    return {
      seasonId,
      competitionId,
      teamId,
      playerId,
      playerPosition,
      leftAggregationDataSource,
      aggregationDataSource
    }
  }

  private _resolvePlayerComparison(
    params: Params,
    position: PlayerPosition,
    isMatch: boolean,
    aggregation: AggregationSelectable
  ) {
    const { players, matchId } = preparePlayerComparison(params, position, aggregation)

    if (isMatch) {
      return fetchMatchPlayerComparisonMetrics({ matchId, comparison: players, position })
    } else {
      return fetchPlayerComparisonMetrics({ players })
    }
  }

  private _resolveSinglePlayer(
    params: Params,
    playerPosition: string,
    isMatch: boolean,
    leftAggregation: AggregationSelectable,
    rightAggregation: AggregationSelectable
  ) {
    const { matchId, teamId, id, seasonId, competitionId } = params

    if (isMatch) {
      return fetchMatchPlayerMetrics({
        matchId,
        teamId,
        playerId: id,
        playerPosition: playerPosition as PlayerPosition,
        leftAggregationDataSource: leftAggregation.id,
        aggregationDataSource: rightAggregation.id
      })
    } else {
      return fetchFavTeamPlayerMetrics({
        seasonId,
        competitionId,
        teamId,
        playerId: id,
        playerPosition,
        leftAggregationDataSource: leftAggregation.id,
        aggregationDataSource: rightAggregation.id
      })
    }
  }

  private _parseComparedPlayer(
    comparedPlayer: Player & {
      team: Team
    },
    requestedPlayer: PlayerComparisonRequestItem,
    team?: Team
  ) {
    return {
      ...comparedPlayer,
      playerId: requestedPlayer.id,
      name: comparedPlayer.formattedName,
      metricPlayerName: `${comparedPlayer.shirtNumber}. ${comparedPlayer.formattedName}`,
      teamId: requestedPlayer.teamId,
      team: { ...team, teamId: requestedPlayer.teamId },
      competitionId: requestedPlayer.competitionId,
      seasonId: requestedPlayer.seasonId,
      ...parseStatics(comparedPlayer.statics, [StaticOrientation.Portrait])
    }
  }

  private _parsePlayerComparison(
    comparison: PlayerComparison,
    comparisonRequest: PlayerComparisonRequest,
    teamA: Team
  ): PlayerComparison {
    return {
      ...comparison,
      playerA: this._parseComparedPlayer(comparison.playerA, comparisonRequest.playerA, teamA),
      playerB: this._parseComparedPlayer(comparison.playerB, comparisonRequest.playerB)
    }
  }
}
